/**
 * Saved audit groups on the Trigger audit screen (Bettina, 2026-10-28).
 *
 * GroupBar sits in the Scope section: load a saved group of states or AF#s into
 * the form, save the current selection as a group, save changes after adding or
 * removing locations, rename, delete. GroupSchedule (admins) schedules the
 * loaded group through the existing scheduler — which only fires while the
 * server's master switch is on (Chad's).
 */

import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { api, ApiError } from '../lib/api';

export interface GroupSchedule {
  id: number;
  enabled: boolean;
  armed: boolean;
  spec: { frequency: 'daily' | 'weekly' | 'monthly'; time: string; weekdays: number[]; month_day: number } | null;
  timezone: string;
  audit_window_days: number;
  notify_email: string | null;
  last_run_at: string | null;
  last_job_id: number | null;
  last_job_status: string | null;
  last_skip_reason: string | null;
}

export interface AuditGroup {
  id: number;
  name: string;
  kind: 'states' | 'afs';
  members: string[];
  schedule: GroupSchedule | null;
}

export interface GroupsResponse { groups: AuditGroup[]; scheduler_enabled: boolean }

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TIMEZONES: { tz: string; label: string }[] = [
  { tz: 'America/New_York', label: 'Eastern' },
  { tz: 'America/Chicago', label: 'Central' },
  { tz: 'America/Denver', label: 'Mountain' },
  { tz: 'America/Phoenix', label: 'Arizona' },
  { tz: 'America/Los_Angeles', label: 'Pacific' },
];

const errText = (e: unknown) => e instanceof ApiError && (e.body as { error?: string })?.error
  ? (e.body as { error: string }).error
  : (e instanceof Error ? e.message : 'failed');

const sameList = (a: string[], b: string[]) => {
  const x = [...a].map((s) => s.toUpperCase()).sort();
  const y = [...b].map((s) => s.toUpperCase()).sort();
  return x.length === y.length && x.every((v, i) => v === y[i]);
};

function to12h(t: string) {
  const [h, m] = t.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

export function describeSchedule(s: GroupSchedule | null): string {
  if (!s?.spec) return 'not scheduled';
  const tz = TIMEZONES.find((t) => t.tz === s.timezone)?.label ?? s.timezone;
  const when = s.spec.frequency === 'daily' ? 'daily'
    : s.spec.frequency === 'weekly' ? `weekly ${s.spec.weekdays.map((d) => DAYS[d]).join('/')}`
    : `monthly on day ${s.spec.month_day}`;
  return `${when} at ${to12h(s.spec.time)} ${tz}${s.enabled ? '' : ' (paused)'}`;
}

export function GroupBar({
  groups, activeId, kind, members, onLoad, onChanged,
}: {
  groups: AuditGroup[];
  activeId: number | null;
  kind: 'states' | 'afs';
  members: string[];
  onLoad: (g: AuditGroup | null) => void;
  onChanged: (activeId: number | null) => void;
}) {
  const active = groups.find((g) => g.id === activeId) ?? null;
  const dirty = !!active && (active.kind !== kind || !sameList(active.members, members));
  const [naming, setNaming] = useState<'new' | 'rename' | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async (fn: () => Promise<number | null>) => {
    setBusy(true); setErr(null);
    try { onChanged(await fn()); setNaming(null); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  const saveNew = () => run(async () => {
    const r = await api<{ id: number }>('/api/audit-groups', { method: 'POST', body: { name: name.trim(), kind, members } });
    return r.id;
  });
  const rename = () => run(async () => {
    await api(`/api/audit-groups/${active!.id}`, { method: 'PUT', body: { name: name.trim() } });
    return active!.id;
  });
  const saveChanges = () => run(async () => {
    await api(`/api/audit-groups/${active!.id}`, { method: 'PUT', body: { kind, members } });
    return active!.id;
  });
  const remove = () => {
    if (!active) return;
    const sched = active.schedule ? '\n\nIts schedule will be removed too.' : '';
    if (!confirm(`Delete the group "${active.name}"?${sched}`)) return;
    void run(async () => { await api(`/api/audit-groups/${active.id}`, { method: 'DELETE' }); return null; });
  };

  return (
    <div style={{ marginBottom: 14, padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <label className="form-lbl" style={{ margin: 0 }}>Saved group</label>
        <select
          className="select"
          style={{ flex: 1, minWidth: 220 }}
          value={activeId ?? ''}
          onChange={(e) => {
            const g = groups.find((x) => x.id === Number(e.target.value)) ?? null;
            setNaming(null); setErr(null);
            onLoad(g);
          }}
        >
          <option value="">— None (pick locations below) —</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name} · {g.members.length} {g.kind === 'states' ? 'state' : 'AF#'}{g.members.length === 1 ? '' : 's'} · {describeSchedule(g.schedule)}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        {active && (
          <button className="btn btn-sm" disabled={!dirty || busy || members.length === 0} onClick={saveChanges}
            style={dirty ? { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' } : undefined}>
            Save changes to “{active.name}”
          </button>
        )}
        <button className="btn btn-sm btn-ghost" disabled={busy || members.length === 0}
          onClick={() => { setNaming('new'); setName(''); }}>
          <Icon name="plus" size={12} /> Save as new group
        </button>
        {active && (
          <>
            <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => { setNaming('rename'); setName(active.name); }}>Rename</button>
            <button className="btn btn-sm btn-ghost" disabled={busy} onClick={remove}>Delete</button>
          </>
        )}
      </div>

      {dirty && (
        <div className="c-warn" style={{ fontSize: 12, marginTop: 8 }}>
          You've changed the locations since loading “{active!.name}”. A run triggered now uses what's selected below;
          save changes to keep them in the group{active!.schedule ? ' (its schedule audits the saved list)' : ''}.
        </div>
      )}

      {naming && (
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <input className="input" autoFocus placeholder="Group name, e.g. Southeast monthly" value={name} maxLength={64}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) void (naming === 'new' ? saveNew() : rename()); }}
            style={{ flex: 1 }} />
          <button className="btn btn-sm" disabled={!name.trim() || busy} onClick={naming === 'new' ? saveNew : rename}>
            {naming === 'new' ? 'Save group' : 'Rename'}
          </button>
          <button className="btn btn-sm btn-ghost" onClick={() => setNaming(null)}>Cancel</button>
        </div>
      )}
      {naming === 'new' && (
        <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
          Saves the {kind === 'states' ? `${members.length} state(s)` : `${members.length} AF#(s)`} selected below: {members.join(', ') || '—'}
        </div>
      )}
      {err && <div className="c-danger" style={{ fontSize: 12, marginTop: 8 }}>{err}</div>}
    </div>
  );
}

export function GroupScheduleForm({
  group, schedulerEnabled, myEmail, isAdmin, onChanged,
}: {
  group: AuditGroup;
  schedulerEnabled: boolean;
  myEmail: string | null;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const s = group.schedule;
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>(s?.spec?.frequency ?? 'weekly');
  const [time, setTime] = useState(s?.spec?.time ?? '06:00');
  const [weekdays, setWeekdays] = useState<number[]>(s?.spec?.weekdays?.length ? s.spec.weekdays : [1]);
  const [monthDay, setMonthDay] = useState(s?.spec?.month_day ?? 1);
  const [timezone, setTimezone] = useState(s?.timezone ?? 'America/New_York');
  const [windowDays, setWindowDays] = useState(s?.audit_window_days ?? 90);
  const [enabled, setEnabled] = useState(s?.enabled ?? true);
  const [notify, setNotify] = useState(s ? !!s.notify_email : true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // Reset the form when a different group is loaded.
  useEffect(() => {
    setFrequency(s?.spec?.frequency ?? 'weekly'); setTime(s?.spec?.time ?? '06:00');
    setWeekdays(s?.spec?.weekdays?.length ? s.spec.weekdays : [1]); setMonthDay(s?.spec?.month_day ?? 1);
    setTimezone(s?.timezone ?? 'America/New_York'); setWindowDays(s?.audit_window_days ?? 90);
    setEnabled(s?.enabled ?? true); setNotify(s ? !!s.notify_email : true); setErr(null); setMsg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);

  const call = async (fn: () => Promise<string>) => {
    setBusy(true); setErr(null); setMsg(null);
    try { setMsg(await fn()); onChanged(); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  const save = () => call(async () => {
    await api(`/api/audit-groups/${group.id}/schedule`, {
      method: 'PUT',
      body: {
        frequency, time, weekdays, month_day: monthDay, timezone, audit_window_days: windowDays, enabled,
        notify_email: notify && myEmail ? myEmail : null,
      },
    });
    return 'Schedule saved.';
  });
  const remove = () => {
    if (!confirm(`Remove the schedule for "${group.name}"? The group itself stays.`)) return;
    void call(async () => { await api(`/api/audit-groups/${group.id}/schedule`, { method: 'DELETE' }); return 'Schedule removed.'; });
  };
  const runNow = () => {
    if (!confirm(`Queue an audit of "${group.name}" now?\n\n${group.members.join(', ')}\nWindow: last ${s?.audit_window_days} day(s)`)) return;
    void call(async () => {
      const r = await api<{ jobId: number }>(`/api/audit-groups/${group.id}/schedule/run`, { method: 'POST' });
      return `Queued audit job #${r.jobId}. Watch it in the banner above or on Audit runs.`;
    });
  };

  if (!isAdmin) {
    return (
      <div className="muted" style={{ fontSize: 12.5 }}>
        {s ? `Scheduled ${describeSchedule(s)}, last ${s.audit_window_days} days.` : 'Not scheduled.'} Only an admin can change a group's schedule.
      </div>
    );
  }

  const toggleDay = (d: number) => setWeekdays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());

  return (
    <div>
      <div style={{
        fontSize: 12.5, padding: '8px 10px', marginBottom: 12, borderRadius: 'var(--radius)',
        borderLeft: `3px solid var(--${schedulerEnabled ? 'ok' : 'warn'})`, background: 'var(--surface-2)',
      }}>
        {schedulerEnabled
          ? <><b>Scheduling is on.</b> An enabled schedule runs on its own at the time below.</>
          : <><b>Scheduling is switched off for the whole tool</b> (Chad's master switch), so saved schedules won't run on
            their own yet. “Run now” works today.</>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, alignItems: 'end' }}>
        <div>
          <label className="form-lbl">How often</label>
          <select className="select" value={frequency} onChange={(e) => setFrequency(e.target.value as typeof frequency)}>
            <option value="daily">Every day</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        {frequency === 'monthly' && (
          <div>
            <label className="form-lbl">Day of month</label>
            <select className="select" value={monthDay} onChange={(e) => setMonthDay(Number(e.target.value))}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="form-lbl">Time</label>
          <input type="time" className="input" value={time} onChange={(e) => setTime(e.target.value)} />
        </div>
        <div>
          <label className="form-lbl">Time zone</label>
          <select className="select" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {TIMEZONES.map((t) => <option key={t.tz} value={t.tz}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="form-lbl">Window (days back)</label>
          <select className="select" value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))}>
            {[1, 2, 7, 30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>Last {d} day{d === 1 ? '' : 's'}</option>)}
          </select>
        </div>
      </div>

      {frequency === 'weekly' && (
        <div style={{ display: 'flex', gap: 4, marginTop: 10, flexWrap: 'wrap' }}>
          {DAYS.map((d, i) => (
            <button key={d} className="btn btn-sm" onClick={() => toggleDay(i)} style={weekdays.includes(i)
              ? { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' } : undefined}>{d}</button>
          ))}
        </div>
      )}
      {frequency === 'monthly' && (
        <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>Days 1–28 only, so the run happens in every month.</div>
      )}

      <div style={{ display: 'flex', gap: 18, marginTop: 12, flexWrap: 'wrap', fontSize: 13 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
          Schedule on (untick to pause)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
          Email me when each scheduled run completes {myEmail && <span className="muted">({myEmail})</span>}
        </label>
      </div>
      <div className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>
        Scheduled runs sync Zoho and pull fresh AxisCare data first, never ignore suppressions, and audit the group's
        saved list as it is when the run starts. If another audit or legacy check is still going, that run is skipped
        (shown below), not stacked. They use the tool's AxisCare login, so if you are signed into AxisCare as the same
        user at that time, one of you gets signed out.
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
        <button className="btn btn-sm" disabled={busy || (frequency === 'weekly' && weekdays.length === 0)} onClick={save}
          style={{ background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' }}>
          {s ? 'Save schedule' : 'Create schedule'}
        </button>
        {s && <button className="btn btn-sm btn-ghost" disabled={busy} onClick={runNow}><Icon name="play" size={12} /> Run now</button>}
        {s && <button className="btn btn-sm btn-ghost" disabled={busy} onClick={remove}>Remove schedule</button>}
      </div>
      {msg && <div className="c-ok" style={{ fontSize: 12, marginTop: 8 }}>{msg}</div>}
      {err && <div className="c-danger" style={{ fontSize: 12, marginTop: 8 }}>{err}</div>}
      {s && (s.last_run_at || s.last_skip_reason) && (
        <div className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>
          {s.last_run_at && <>Last run {new Date(s.last_run_at).toLocaleString()}{s.last_job_id ? ` · job #${s.last_job_id}` : ''}{s.last_job_status ? ` (${s.last_job_status})` : ''}</>}
          {s.last_skip_reason && <span className="c-warn"> · Last attempt skipped: {s.last_skip_reason}</span>}
        </div>
      )}
    </div>
  );
}
